/**
 * TODO API Service
 *
 * Thin wrapper around the shared axios instance for the User TODO List feature.
 * The default `axios` import is used so requests flow through the global auth
 * interceptor configured in `setupAxiosAuth.js`, which attaches the MSAL bearer
 * token and handles 401/403 responses. Endpoint paths come from the centralized
 * `API_ENDPOINTS` registry in `config/apiConfig.js`.
 */

import axios from 'axios';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';

/**
 * List the authenticated user's tasks (newest first, capped server-side at 500).
 * When a projectId is provided, only that project's tasks are returned.
 * @param {string} [projectId] - Optional project to scope the tasks to.
 * @returns {Promise<{ tasks: Array, count: number }>} Response payload.
 */
export const listTasks = async (projectId) => {
  const url = projectId
    ? buildApiUrl(API_ENDPOINTS.todosByProject(projectId))
    : buildApiUrl(API_ENDPOINTS.todos);
  const response = await axios.get(url);
  return response.data;
};

/**
 * List the authenticated user's projects (oldest first). The backend ensures a
 * default project always exists.
 * @returns {Promise<{ projects: Array, count: number }>} Response payload.
 */
export const listProjects = async () => {
  const response = await axios.get(buildApiUrl(API_ENDPOINTS.todoProjects));
  return response.data;
};

/**
 * Create a new project owned by the authenticated user, optionally with a
 * daily work window (startTime/endTime as HH:MM).
 * @param {string} name - Project name (1-100 chars).
 * @param {{ startTime?: string, endTime?: string }} [schedule] - Optional window.
 * @returns {Promise<{ project: Object }>} Response payload.
 */
export const createProject = async (name, schedule = {}) => {
  const body = { name };
  if (schedule.startTime || schedule.endTime) {
    body.startTime = schedule.startTime || null;
    body.endTime = schedule.endTime || null;
  }
  const response = await axios.post(buildApiUrl(API_ENDPOINTS.todoProjects), body);
  return response.data;
};

/**
 * Update a project's name and/or daily work window.
 * @param {string} projectId - Identifier of the project to update.
 * @param {{ name?: string, startTime?: string|null, endTime?: string|null }} fields
 * @returns {Promise<{ project: Object }>} Response payload.
 */
export const updateProject = async (projectId, fields) => {
  const response = await axios.put(buildApiUrl(API_ENDPOINTS.todoProject(projectId)), fields);
  return response.data;
};

/**
 * Rename an existing project (convenience wrapper over updateProject).
 * @param {string} projectId - Identifier of the project to rename.
 * @param {string} name - New project name.
 * @returns {Promise<{ project: Object }>} Response payload.
 */
export const renameProject = async (projectId, name) => {
  return updateProject(projectId, { name });
};

/**
 * Set or clear a project's daily work window. Pass null/empty for both to clear.
 * @param {string} projectId - Identifier of the project.
 * @param {string|null} startTime - HH:MM start, or null to clear.
 * @param {string|null} endTime - HH:MM end, or null to clear.
 * @returns {Promise<{ project: Object }>} Response payload.
 */
export const setProjectSchedule = async (projectId, startTime, endTime) => {
  return updateProject(projectId, {
    startTime: startTime || null,
    endTime: endTime || null
  });
};

/**
 * Delete a project and all of its tasks.
 * @param {string} projectId - Identifier of the project to delete.
 * @returns {Promise<{ projectId: string, deletedTaskCount: number }>} Response payload.
 */
export const deleteProject = async (projectId) => {
  const response = await axios.delete(buildApiUrl(API_ENDPOINTS.todoProject(projectId)));
  return response.data;
};

/**
 * Create a new task owned by the authenticated user.
 * @param {Object} payload - Task fields (title, description, deadline, priority, status).
 * @returns {Promise<{ task: Object }>} Response payload with the created task.
 */
export const createTask = async (payload) => {
  const response = await axios.post(buildApiUrl(API_ENDPOINTS.todos), payload);
  return response.data;
};

/**
 * Edit an existing task's title, description, deadline, and priority.
 * @param {string} taskId - Identifier of the task to edit.
 * @param {Object} payload - Updated task fields.
 * @returns {Promise<{ task: Object }>} Response payload with the updated task.
 */
export const editTask = async (taskId, payload) => {
  const response = await axios.put(buildApiUrl(API_ENDPOINTS.todo(taskId)), payload);
  return response.data;
};

/**
 * Change a task's status (Completed, In Progress, or Backlog).
 * @param {string} taskId - Identifier of the task to update.
 * @param {string} status - Target status value.
 * @returns {Promise<{ task: Object }>} Response payload with the updated task.
 */
export const changeStatus = async (taskId, status) => {
  const response = await axios.put(buildApiUrl(API_ENDPOINTS.todoStatus(taskId)), { status });
  return response.data;
};

/**
 * Delete a task owned by the authenticated user.
 * @param {string} taskId - Identifier of the task to delete.
 * @returns {Promise<{ message: string, taskId: string }>} Response payload.
 */
export const deleteTask = async (taskId) => {
  const response = await axios.delete(buildApiUrl(API_ENDPOINTS.todo(taskId)));
  return response.data;
};

/**
 * Move a task to a different project.
 * @param {string} taskId - Identifier of the task to move.
 * @param {string} projectId - Target project identifier.
 * @returns {Promise<{ task: Object }>} Response payload.
 */
export const moveTask = async (taskId, projectId) => {
  const response = await axios.put(buildApiUrl(API_ENDPOINTS.todoMove(taskId)), { projectId });
  return response.data;
};

const todoApi = {
  listTasks,
  createTask,
  editTask,
  changeStatus,
  deleteTask,
  listProjects,
  createProject,
  updateProject,
  renameProject,
  setProjectSchedule,
  deleteProject,
  moveTask
};

export default todoApi;
